import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, mediaUrl } from "@/lib/api";
import { youtubeEmbedUrl } from "@/lib/youtube";
import { formatDate } from "@/lib/format";
import { renderRich } from "@/lib/rich";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ShareBar } from "@/components/ShareBar";
import { CelebrationGiraffe } from "@/components/CelebrationGiraffe";
import { CoffeeSupport } from "@/components/CoffeeSupport";
import { ArticleSponsors } from "@/components/ArticleSponsors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, MessageCircle, Trash2 } from "lucide-react";
import { applySeo, applyNotFoundSeo, SITE_URL } from "@/lib/seo";
import { LostGiraffe } from "@/components/LostGiraffe";
import { slugify } from "@/lib/slug";

const firstText = (post) => {
  if (post.excerpt && post.excerpt.trim()) return post.excerpt.trim();
  const p = (post.content || []).find((b) => b.type === "paragraph" && b.text);
  return p ? p.text.replace(/\*\*/g, "").trim() : "";
};

const Block = ({ block, index }) => {
  if (block.type === "heading")
    return (
      <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#1C1917] mt-12 mb-4">
        {renderRich(block.text)}
      </h2>
    );
  if (block.type === "quote")
    return (
      <blockquote className="border-l-2 border-[#9F583F] pl-6 my-10 font-display italic text-2xl md:text-3xl leading-snug text-[#1C1917]">
        {renderRich(block.text)}
      </blockquote>
    );
  if (block.type === "image")
    return (
      <figure className="my-10">
        <img
          src={mediaUrl(block.url)}
          alt={block.caption || ""}
          className="w-full object-cover rounded-sm"
        />
        {block.caption && (
          <figcaption className="label-ui text-[#57534E] mt-3 text-center">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  if (block.type === "video") {
    const embed = youtubeEmbedUrl(block.url);
    if (!embed) return null;
    return (
      <figure className="my-10">
        <div className="relative w-full aspect-video bg-black rounded-sm overflow-hidden">
          <iframe
            src={embed}
            title={block.caption || "Video YouTube"}
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            data-testid="article-video"
          />
        </div>
        {block.caption && (
          <figcaption className="label-ui text-[#57534E] mt-3 text-center">
            {block.caption}
          </figcaption>
        )}
      </figure>
    );
  }
  return (
    <p className={`font-body text-lg leading-loose text-[#292524] ${index === 0 ? "drop-cap" : ""}`}>
      {renderRich(block.text)}
    </p>
  );
};

export default function Article() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [comments, setComments] = useState([]);
  const [related, setRelated] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [content, setContent] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [celebrate, setCelebrate] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get(`/posts/${slug}`)
      .then((res) => setPost(res.data))
      .catch(() => setNotFound(true));
    api
      .get(`/posts/${slug}/comments`)
      .then((res) => setComments(res.data))
      .catch(() => {});
  }, [slug]);

  // Register a real read: once per browser per article, and never for the admin.
  useEffect(() => {
    if (!post || loading || user) return;
    const key = `pp_viewed_${slug}`;
    if (localStorage.getItem(key)) return;
    localStorage.setItem(key, "1");
    api
      .post(`/posts/${slug}/view`)
      .then((res) => setPost((p) => (p ? { ...p, views: res.data.views } : p)))
      .catch(() => {});
  }, [post, user, loading, slug]);

  // Per-article SEO: unique title/description/canonical/OG + JSON-LD, derived
  // deterministically from the CURRENT slug so nothing is ever inherited from
  // a previously visited article.
  useEffect(() => {
    if (!post) return;
    const canonicalPath = `/articolo/${slug}`;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const desc = firstText(post).slice(0, 300);
    const image = post.cover_image
      ? (/^https?:\/\//i.test(post.cover_image) ? post.cover_image : SITE_URL + post.cover_image)
      : undefined;
    applySeo({
      path: canonicalPath,
      title: `${post.title} — Il Cassetto delle Giraffe`,
      description: desc,
      image,
      type: "article",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          headline: post.title,
          description: desc || undefined,
          image: image ? [image] : undefined,
          datePublished: post.published_at || post.created_at || undefined,
          dateModified: post.updated_at || post.published_at || post.created_at || undefined,
          author: { "@type": "Person", name: post.author || "Fabio Campana" },
          publisher: { "@type": "Organization", name: "Il Cassetto delle Giraffe" },
          mainEntityOfPage: { "@type": "WebPage", "@id": canonicalUrl },
          url: canonicalUrl,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
            ...(post.category
              ? [{ "@type": "ListItem", position: 2, name: post.category, item: SITE_URL + "/giraffe-map" }]
              : []),
            { "@type": "ListItem", position: post.category ? 3 : 2, name: post.title, item: canonicalUrl },
          ],
        },
      ],
    });
  }, [post, slug]);

  // A deleted / non-existent article must be kept out of Google's index:
  // strong noindex, no canonical, no Article structured data.
  useEffect(() => {
    if (notFound) applyNotFoundSeo(`/articolo/${slug}`);
  }, [notFound, slug]);

  // Auto-linked related articles (same category, then shared tags, then recent).
  useEffect(() => {
    setRelated([]);
    api.get(`/posts/${slug}/related`).then((r) => setRelated(r.data || [])).catch(() => {});
  }, [slug]);

  const deleteComment = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      setComments((c) => c.filter((x) => x.id !== id));
      toast.success("Commento eliminato");
    } catch {
      toast.error("Impossibile eliminare il commento");
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !content.trim()) {
      toast.error("Inserisci nome e cognome, email e commento");
      return;
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.error("Inserisci un'email valida");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/posts/${slug}/comments`, { name, email, content });
      setComments((c) => [data, ...c]);
      setName("");
      setEmail("");
      setContent("");
      toast.success("Commento pubblicato. Grazie!");
      setCelebrate(true);
      setTimeout(() => setCelebrate(false), 4500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossibile inviare il commento");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound)
    return (
      <div className="relative z-10">
        <Header />
        <div className="max-w-3xl mx-auto px-6 py-40 text-center">
          <h1 className="font-display text-5xl text-[#1C1917] italic">Articolo non trovato</h1>
          <Link to="/" className="inline-block mt-8 label-ui text-[#9F583F] link-underline">
            Torna agli articoli
          </Link>
        </div>
        <Footer />
      </div>
    );

  if (!post)
    return (
      <div className="relative z-10">
        <Header />
        <p className="max-w-3xl mx-auto px-6 py-40 font-ui text-sm text-[#57534E]">
          Caricamento…
        </p>
      </div>
    );

  return (
    <div className="relative z-10">
      <Header />

      {/* Cover */}
      {post.cover_image && (
        <div className="w-full max-h-[70vh] overflow-hidden border-b border-[#E7E5E4]">
          <img
            src={mediaUrl(post.cover_image)}
            alt={post.title}
            className="w-full h-full object-cover max-h-[70vh]"
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 md:px-8 pt-14 pb-10 relative" data-testid="article-content">
        <ArticleSponsors />
        <LostGiraffe slug={slug} />
        <Link to="/" className="inline-flex items-center gap-2 label-ui text-[#57534E] hover:text-[#9F583F] transition-colors mb-10">
          <ArrowLeft size={14} strokeWidth={1.5} /> Tutti gli articoli
        </Link>

        <div className="flex flex-wrap items-center gap-3 mb-6">
          {post.tags?.map((t) => (
            <span key={t} className="label-ui text-[#9F583F] border border-[#E7E5E4] px-3 py-1">
              {t}
            </span>
          ))}
        </div>

        {post.category && (
          <Link
            to={`/categoria/${slugify(post.category)}`}
            data-testid="article-category-link"
            className="inline-block label-ui text-[#9F583F] hover:text-[#1C1917] transition-colors mb-4"
          >
            {post.category}
          </Link>
        )}
        <h1 className="font-display text-4xl md:text-6xl leading-[1] tracking-tight text-[#1C1917]">
          {post.title}
        </h1>
        {post.excerpt && (
          <div className="mt-7">
            <div className="w-14 h-[3px] bg-[#9F583F] rounded-full mb-5" />
            <p className="font-body not-italic text-lg md:text-xl text-[#78716C] leading-relaxed">
              {post.excerpt}
            </p>
          </div>
        )}

        <div className="flex items-center gap-4 mt-8 pb-10 border-b border-[#E7E5E4]">
          <span className="label-ui text-[#57534E]">di {post.author || "Fabio Campana"}</span>
        </div>

        {/* Body */}
        <div className="article-body mt-10">
          {(post.content || []).map((b, i) => (
            <Block key={i} block={b} index={i} />
          ))}
        </div>

        {/* Share */}
        <div className="mt-16 pt-10 border-t border-[#E7E5E4] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <ShareBar title={post.title} orientation="horizontal" />
          {user && (
            <span className="label-ui text-[#A8A29E]" data-testid="views-count">{post.views || 0} letture</span>
          )}
        </div>
      </article>

      {/* Il Sentiero delle Giraffe — related articles (auto-linked) */}
      {related.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 md:px-10 pt-6 pb-4" data-testid="related-posts">
          <div className="border-t border-[#E7E5E4] pt-12">
            <div className="flex items-center gap-2 mb-8">
              <span className="label-ui text-[#9F583F]">Il Sentiero delle Giraffe</span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl tracking-tight text-[#1C1917] mb-10">
              Continua a esplorare
            </h2>
            <div className="grid md:grid-cols-3 gap-x-8 gap-y-12">
              {related.map((r, i) => (
                <motion.div
                  key={r.slug}
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.5, delay: (i % 3) * 0.09, ease: "easeOut" }}
                >
                  <Link
                    to={`/articolo/${r.slug}`}
                    data-testid={`related-post-${r.slug}`}
                    className="group flex flex-col transition-transform duration-300 ease-out hover:-translate-y-1.5"
                  >
                    <div className="overflow-hidden mb-5 relative bg-[#E7E5E4]">
                      {r.cover_image ? (
                        <img
                          src={mediaUrl(r.cover_image)}
                          alt={r.title}
                          loading="lazy"
                          className="w-full aspect-[3/2] object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                        />
                      ) : (
                        <div className="w-full aspect-[3/2] flex items-center justify-center">
                          <span className="font-display italic text-2xl text-[#A8A29E]">Giraffe</span>
                        </div>
                      )}
                      <span className="pointer-events-none absolute inset-0 bg-[#1C1917] opacity-0 group-hover:opacity-[0.06] transition-opacity duration-300" />
                    </div>
                    {r.category && <span className="label-ui text-[#9F583F] mb-2">{r.category}</span>}
                    <h3 className="font-display text-2xl leading-tight tracking-tight text-[#1C1917] group-hover:text-[#9F583F] transition-colors">
                      {r.title}
                    </h3>
                    <span className="inline-flex items-center gap-2 mt-3 label-ui text-[#9F583F] opacity-0 -translate-x-1.5 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300">
                      Leggi l'articolo <ArrowRight size={14} strokeWidth={1.6} />
                    </span>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Support / Buy me a coffee */}
      <div className="max-w-3xl mx-auto px-6 md:px-8">
        <CoffeeSupport />
      </div>

      {/* Comments */}
      <section className="max-w-3xl mx-auto px-6 md:px-8 pb-20" data-testid="comments-section">
        <div className="flex items-center gap-3 mt-10 mb-10">
          <MessageCircle size={20} strokeWidth={1.5} className="text-[#9F583F]" />
          <h2 className="font-display text-3xl tracking-tight text-[#1C1917]">
            Commenti <span className="text-[#A8A29E]">({comments.length})</span>
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={submitComment} className="mb-14" data-testid="comment-form">
          <Input
            data-testid="comment-name-input"
            placeholder="Nome e cognome"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="font-body rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] text-lg mb-5"
          />
          <Input
            data-testid="comment-email-input"
            type="email"
            placeholder="La tua email (non verrà pubblicata)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="font-body rounded-none border-x-0 border-t-0 border-b border-[#E7E5E4] bg-transparent px-0 focus-visible:ring-0 focus-visible:border-[#9F583F] text-lg mb-5"
          />
          <Textarea
            data-testid="comment-content-input"
            placeholder="Scrivi un commento…"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            className="font-body rounded-none border border-[#E7E5E4] bg-transparent focus-visible:ring-0 focus-visible:border-[#9F583F] text-lg"
          />
          <Button
            type="submit"
            disabled={submitting}
            data-testid="comment-submit-button"
            className="mt-5 rounded-none bg-[#1C1917] hover:bg-[#9F583F] text-white label-ui px-8 py-6 h-auto transition-colors"
          >
            {submitting ? "Invio…" : "Pubblica commento"}
          </Button>
        </form>

        {/* List */}
        <div className="divide-y divide-[#E7E5E4] border-t border-[#E7E5E4]">
          {comments.length === 0 ? (
            <p className="font-body italic text-[#57534E] py-8">
              Nessun commento. Sii il primo a scrivere.
            </p>
          ) : (
            comments.map((c) => (
              <div key={c.id} className="py-7 group" data-testid="comment-item">
                <div className="flex items-baseline justify-between gap-4 mb-2">
                  <span className="font-display text-xl text-[#1C1917]">{c.name}</span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="label-ui text-[#A8A29E]">{formatDate(c.created_at)}</span>
                    {user && (
                      <button
                        onClick={() => deleteComment(c.id)}
                        data-testid={`delete-comment-${c.id}`}
                        title="Elimina commento"
                        className="text-[#A8A29E] hover:text-red-600 transition-colors"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    )}
                  </div>
                </div>
                <p className="font-body text-[#292524] leading-relaxed whitespace-pre-wrap">
                  {c.content}
                </p>
              </div>
            ))
          )}
        </div>
      </section>

      <CelebrationGiraffe active={celebrate} />
      <Footer />
    </div>
  );
}
